import { ChartMargin } from "../interfaces/chart-margin";
import { Axis, axisBottom, axisLeft } from "d3-axis";
import { ScaleBand, ScaleLinear } from "d3-scale";
import { Selection } from "d3-selection";
import {
    ChartDataGroupConfig,
    BarChartDataGroupConfigItem,
} from "../interfaces/chart-data-group-config";
import { ChartData, ChartGroupDataValues } from "../interfaces/chart-data";
import { BarChartGroupItem, TargetValues } from "./bar-chart";
import { BarChartSvgDataGroup } from "./bar-chart-svg-data-group";
import { transform } from "../../../utils/transform";
import { Transition } from "d3-transition";

export class BarChartSvgChartArea {
    selection: Selection<SVGGElement, unknown, HTMLElement, any>;

    yAxisArea: Selection<SVGGElement, unknown, HTMLElement, any>;
    xAxisArea: Selection<SVGGElement, unknown, HTMLElement, any>;

    constructor(svg, margin) {
        this.selection = svg
            .append("g")
            .attr(
                "transform",
                "translate(" + margin.left + "," + margin.top + ")"
            );
    }

    public getXAxisArea() {
        return this.xAxisArea;
    }

    public getYAxisArea() {
        return this.yAxisArea;
    }

    public appendYAxis() {
        this.yAxisArea = this.selection
            .append("g")
            .attr("class", "uic-bar-chart__y-axis");
        return this.yAxisArea;
    }

    public appendXAxis(parentHeight, margin: ChartMargin) {
        this.xAxisArea = this.selection
            .append("g")
            .attr("class", "uic-bar-chart__x-axis")
            .attr(
                "transform",
                `translate(0,${parentHeight - margin.bottom - margin.top})`
            );
        return this.xAxisArea;
    }

    public appendDataGroupArea() {
        this.selection.append("g").attr("class", "uic-bar-chart");
        this.selection
            .append("g")
            .attr("class", "uic-bar-chart__target-values");
    }

    public updateChartArea(
        margin: ChartMargin,
        yAxisLabelOffset: number,
        maxYTickLabelWidth: number
    ) {
        this.selection.attr(
            "transform",
            `translate(${
                margin.left + yAxisLabelOffset + maxYTickLabelWidth
            }, ${margin.top})`
        );

        return this;
    }

    public updateYAxis(
        yScale: ScaleLinear<number, number>,
        yTicks: number,
        yFormatter: (y: number) => string,
        xAxisWidth: number,
        transitionDurationMs: number
    ) {
        this.selection
            .select<SVGGElement>(".uic-bar-chart__y-axis")
            .transition()
            .duration(transitionDurationMs)
            .call(
                BarChartSvgChartArea.yAxis(
                    yScale,
                    yTicks,
                    yFormatter,
                    xAxisWidth
                )
            )
            .selectAll<SVGLineElement, number>("line")
            .selection()
            .classed("uic-bar-chart__y-axis-grid-line", true)
            // highlight reference line for 0 if not on the x axis
            .classed(
                "uic-bar-chart__y-axis-grid-line--zero",
                (i) => i === 0 && yScale.domain()[0] < 0
            );

        return this;
    }

    public updateXAxis(
        xScale: ScaleBand<string>,
        parentHeight: number,
        maxXTickLabelWidth: number,
        labelRotationDegrees: number,
        additionalXTickLabelSpace: number,
        margin: ChartMargin,
        xFormatter,
        transitionDurationMs: number
    ) {
        const xAxis = this.selection.select<SVGGElement>(
            ".uic-bar-chart__x-axis"
        );

        xAxis
            .transition()
            .duration(transitionDurationMs)
            .attr(
                "transform",
                `translate(0, ${
                    parentHeight -
                    margin.bottom -
                    margin.top -
                    additionalXTickLabelSpace
                })`
            )
            .call(BarChartSvgChartArea.xAxis(xScale, xFormatter));

        // x axis label direction
        const text = xAxis.selectAll("text");
        if (maxXTickLabelWidth > xScale.bandwidth() * 1.1) {
            // instead of shrinking the chart we may crop the labels if they get too long, maybe use textPath with diagonal line instead as it crops the text automatically
            // see https://stackoverflow.com/questions/9241315/trimming-text-to-a-given-pixel-width-in-svg, https://bl.ocks.org/armollica/90ff1d55f7a8fdbf42500558e1c6d1f5
            text.style("text-anchor", "end")
                .attr("dx", "-.8em")
                .attr("dy", ".15em")
                .attr("transform", `rotate(-${labelRotationDegrees})`);
        } else {
            text.style("text-anchor", "inherit")
                .attr("dy", "0.71em")
                .attr("dx", null)
                .attr("transform", null);
        }

        return this;
    }

    public updateTargetValues(
        groupedData: ChartData,
        targetValues: TargetValues,
        xScale: ScaleBand<string>,
        yScale: ScaleLinear<number, number>,
        transitionDurationMs: number
    ) {
        const orderedTargetValues = Object.keys(groupedData)
            .map((g) => ({ key: g, target: targetValues[g] }))
            .filter((d) => d.target !== undefined);

        const targetValueElements = this.selection
            .select<SVGGElement>(".uic-bar-chart__target-values")
            .selectAll<
                SVGLineElement,
                {
                    key: string;
                    target: number;
                }
            >(".uic-bar-chart__target-value")
            .data(orderedTargetValues, (d) =>
                d != null ? `uic-bar-chart__target-value--${d.key}` : undefined
            );

        targetValueElements
            .enter()
            .append("line")
            .attr("class", "uic-bar-chart__target-value")
            .call((s) =>
                BarChartSvgChartArea.positionTargetValueLines(s, xScale, yScale)
            );

        targetValueElements
            .transition()
            .duration(transitionDurationMs)
            .call((s) =>
                BarChartSvgChartArea.positionTargetValueLines(s, xScale, yScale)
            );

        targetValueElements.exit().remove();

        return this;
    }

    public updateDataGroups(
        groupedData: BarChartGroupItem[],
        groupConfig: ChartDataGroupConfig<
            ChartGroupDataValues,
            BarChartDataGroupConfigItem
        >
    ) {
        const dataGroups = this.selection
            .select(".uic-bar-chart")
            .selectAll<SVGGElement, BarChartGroupItem>(
                ".uic-bar-chart__datagroup"
            )
            .data(groupedData, function (d) {
                return d != null
                    ? `uic-bar-chart__datagroup--${d.key}`
                    : (this as SVGGElement).id;
            });

        const enteringDataGroups = dataGroups.enter().append("g");

        const exisitingDataGroups = enteringDataGroups
            .merge(dataGroups)
            .attr("id", (s) => `uic-bar-chart__datagroup--${s.key}`)
            .attr(
                "class",
                (s) =>
                    `uic-bar-chart__datagroup ${
                        groupConfig[s.key] != undefined
                            ? groupConfig[s.key].cssClass
                            : ""
                    }`
            );

        dataGroups.exit().remove();

        return new BarChartSvgDataGroup(exisitingDataGroups);
    }

    private static xAxis(
        xScale: ScaleBand<string>,
        xFromatter: (x: string) => string
    ): Axis<string> {
        return axisBottom(xScale)
            .tickSizeOuter(0)
            .tickFormat((val) => transform(xFromatter, val));
    }

    private static yAxis(
        yScale: ScaleLinear<number, number>,
        yTicks: number,
        yFormatter: (x: number) => string,
        width: number
    ): Axis<number | { valueOf(): number }> {
        return axisLeft(yScale)
            .ticks(yTicks)
            .tickFormat((val) => transform(yFormatter, val).toString())
            .tickSize(-width);
    }

    private static positionTargetValueLines(
        selection:
            | Selection<
                  SVGLineElement,
                  { key: string; target: number },
                  SVGGElement,
                  unknown
              >
            | Transition<
                  SVGLineElement,
                  { key: string; target: number },
                  SVGGElement,
                  unknown
              >,
        xScale: ScaleBand<string>,
        yScale: ScaleLinear<number, number>
    ) {
        selection
            .attr("data-key", (d) => d.key)
            .attr("x1", (d) => xScale(d.key) - xScale.bandwidth() * 0.05)
            .attr("x2", (d) => xScale(d.key) + xScale.bandwidth() * 1.05)
            .attr("y1", (d) => yScale(d.target))
            .attr("y2", (d) => yScale(d.target));
    }
}
